import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';

export interface NebenkostenArtikelTagsDialogData {
    artikel_id: number;
    bezeichnung?: string;
    kuerzel?: string;
    buchungskonto?: string;
    tags?: string;
}

@Component({
    selector: 'kiene-nebenkosten-artikel-tags-dialog',
    templateUrl: './nebenkosten-artikel-tags-dialog.component.html',
    styleUrls: ['./nebenkosten-artikel-tags-dialog.component.scss'],
})
export class NebenkostenArtikelTagsDialogComponent implements OnInit {
    readonly separatorKeysCodes = [ENTER, COMMA] as const;
    tags: string[] = [];
    tagInputCtrl = new UntypedFormControl('');
    saving = false;

    constructor(
        public dialogRef: MatDialogRef<NebenkostenArtikelTagsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: NebenkostenArtikelTagsDialogData,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.tags = this.parseTags(this.data?.tags);
    }

    get title(): string {
        const bez = (this.data?.bezeichnung || '').trim();
        return bez ? `Tags: ${bez}` : 'Match-Tags bearbeiten';
    }

    addFromInput(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value) {
            this.addTag(value);
        }
        if (event.chipInput) {
            event.chipInput.clear();
        } else if (event.input) {
            event.input.value = '';
        }
        this.tagInputCtrl.setValue('');
    }

    removeTag(tag: string): void {
        this.tags = this.tags.filter(t => t !== tag);
    }

    save(): void {
        if (!this.data?.artikel_id || this.saving) {
            return;
        }
        this.saving = true;
        const body = {
            artikelId: this.data.artikel_id,
            tags: this.tags,
        };
        this.api.post(this.apiBaseUrl + 'fibu_nebenkosten_artikel/update_tags.php', body).subscribe({
            next: () => {
                this.messageService.infoMessage('Tags gespeichert.');
                this.dialogRef.close(true);
            },
            error: err => {
                this.saving = false;
                this.messageService.errorMessage('Tags speichern fehlgeschlagen.', err);
            },
        });
    }

    close(): void {
        this.dialogRef.close(false);
    }

    private addTag(raw: string): void {
        const display = this.prettyTag(raw);
        if (!display || display.length < 2) {
            return;
        }
        const key = this.normalizeKey(display);
        if (this.tags.some(t => this.normalizeKey(t) === key)) {
            return;
        }
        this.tags = [...this.tags, display];
    }

    private parseTags(raw?: string): string[] {
        if (!raw || !raw.trim()) {
            return [];
        }
        return raw
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length >= 2 && !t.includes('?'));
    }

    /** Anzeige mit Umlauten wo bekannt */
    private prettyTag(raw: string): string {
        const n = this.normalizeKey(raw);
        const map: Record<string, string> = {
            heizoel: 'Heizöl',
            broetchen: 'Brötchen',
            fruehstueck: 'Frühstück',
            autowaesche: 'Autowäsche',
            fahrzeugwaesche: 'Fahrzeugwäsche',
            waschstrasse: 'Waschstraße',
            parkgebuehr: 'Parkgebühr',
            kaelte: 'Kälte',
            kueche: 'Küche',
            bundestieraerztekammer: 'Bundestierärztekammer',
            huelsmann: 'Hülsmann',
        };
        if (map[n]) {
            return map[n];
        }
        // User tippt Umlaute → behalten für Anzeige; Speichern normalisiert die API
        return raw.trim();
    }

    private normalizeKey(raw: string): string {
        return raw
            .trim()
            .toLowerCase()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .replace(/\s+/g, ' ');
    }
}
